import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';
import { useParams } from 'react-router-dom';

import { CourseProgressStats } from '@/models/StudentDashboard';
import { card } from '@/styles/styles';
import useAuth from '@/data/hook/useAuth';
import { StudentProgressStats } from './StudentProgressStats';
import SkillsRadar from './SkillsRadar';
import { useStudentProgressStats } from './StudentProgressStats.hooks';
import { ListStudentTagScoreParams } from '@/data/services/studentTagScoreServices';
import { useAreaScore } from '@/pages/skill/SkillPage.hooks';
import ProgressCollectionTitle from '../ProgressCollectionTitle';

type Props = {
  statsCollection: CourseProgressStats;
};

export default function StudentProgressStatsCollection(props: Props) {
  const { statsCollection } = props;
  const { user } = useAuth();
  const stats = useStudentProgressStats(statsCollection);

  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard',
  });

  const { areaId: areaIdParam } = useParams();

  const areaId = Number(areaIdParam);

  const tagScoreFilters: ListStudentTagScoreParams = {
    studentId: user?.id,
    latestOnly: true,
    parentAreaOnly: !areaId,
    parentAreaId: areaId ? areaId : undefined,
  };

  const { areaScores, isLoadingAreas } = useAreaScore(tagScoreFilters);

  const statsLength = useMemo(() => {
    const statsWithTotalGreaterThanZero = Object.values(statsCollection).filter(
      stat => stat.total > 0,
    );
    const baseLength = statsWithTotalGreaterThanZero.length;

    return areaScores.length > 0 ? baseLength + 1 : baseLength;
  }, [statsCollection, areaScores]);

  if (!user) return null;

  return (
    <section className={twMerge(card, 'flex flex-col gap-2.5 p-2 border-none')}>
      <ProgressCollectionTitle title={t('progress')} />
      <ul
        className={twJoin(
          'grid md:flex gap-4',
          statsLength < 3 ? 'xs:flex' : 'xs:grid-cols-2',
        )}
      >
        {Object.entries(stats).map(([key, value]) => (
          <StudentProgressStats key={key} stats={value} />
        ))}

        <SkillsRadar areaScores={areaScores} loading={isLoadingAreas} />
      </ul>
    </section>
  );
}
